import api from './api';

export const AUTH_TOKEN_KEY = 'dc-sign';
export const AUTH_USER_KEY = 'dc-user';

export interface LoginPayload {
  email: string;
  password: string;
}

export interface RegisterPayload {
  email: string;
  firstname: string;
  lastname: string;
  password: string;
  username: string;
}

export interface AuthResponse {
  token?: string | null;
}

export interface AuthUser {
  id?: string | null;
  email?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  username?: string | null;
  [key: string]: any;
}

const decodeJwtPayload = (token: string): AuthUser | null => {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const decoded = atob(padded);
    const json = decodeURIComponent(
      decoded
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const parsed = JSON.parse(json);
    return {
      id: parsed?.id ?? parsed?._id ?? parsed?.userId ?? null,
      email: parsed?.email ?? null,
      firstname: parsed?.firstname ?? parsed?.firstName ?? null,
      lastname: parsed?.lastname ?? parsed?.lastName ?? null,
      username: parsed?.username ?? null,
      ...parsed,
    };
  } catch {
    return null;
  }
};

const persistToken = (token: string) => {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
  const user = decodeJwtPayload(token);
  if (user) {
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
  }
  return user;
};

export const login = async (payload: LoginPayload): Promise<{ token: string; user: AuthUser | null }> => {
  const response = await api.post<AuthResponse>('/api/user/login/v2', payload);
  const token = response.data?.token;
  if (!token) {
    throw new Error('Kein Token vom Server erhalten.');
  }
  const user = persistToken(token);
  return { token, user };
};

export const register = async (payload: RegisterPayload): Promise<{ token: string; user: AuthUser | null }> => {
  const response = await api.post<AuthResponse>('/api/user/create/v2', payload);
  const token = response.data?.token;
  if (!token) {
    throw new Error('Kein Token vom Server erhalten.');
  }
  const user = persistToken(token);
  return { token, user };
};

export const requestPasswordReset = async (email: string): Promise<void> => {
  await api.post('/api/user/reset', { email });
};

/** Passwort-Reset mit dem per Mail versendeten PIN abschließen (wie in der App) */
export const confirmPasswordReset = async (
  email: string,
  pin: string,
  newPassword: string,
): Promise<void> => {
  await api.post('/api/user/reset/confirm', { email, pin, newPassword });
};

export const logout = (): void => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
};

export const getStoredToken = (): string | null => {
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

export const getStoredUser = (): AuthUser | null => {
  const raw = localStorage.getItem(AUTH_USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
};

export const isAuthenticated = (): boolean => {
  return Boolean(getStoredToken());
};

const authService = {
  login,
  register,
  requestPasswordReset,
  confirmPasswordReset,
  logout,
  getStoredToken,
  getStoredUser,
  isAuthenticated,
};

export default authService;
